import { SubCheckoutHeaderProps } from "~/components/SubCheckoutV2/SubCheckoutHeader";
import { SubCheckoutSelectedSubscriptionProps } from "~/components/SubCheckoutV2/SubCheckoutSelectedSubscription";
import { commonConfig } from "~/config/common-config";
import {
  PianoOffer,
  SubscriptionInterval,
  SubscriptionName,
} from "~/types/endil/subscription";

export type SubscriptionDataV3 = {
  /** value used by the account setting overview screen */
  accountOverviewLabel: string;
  pianoOffer: PianoOffer;
  subCheckoutHeaderProps: SubCheckoutHeaderProps;
  subCheckoutSelectedSubscriptionProps: SubCheckoutSelectedSubscriptionProps;
};

const isPianoSandbox = commonConfig.NEXT_PUBLIC_ENDIL_ENV !== "production";

export const subscriptionMapV3 = new Map<SubscriptionName, SubscriptionDataV3>([
  /** Monthly w/o trial */
  [
    SubscriptionName.ONE_SEAT_MONTHLY,
    {
      accountOverviewLabel: "Individual Subscription (1 Seat)",
      pianoOffer: {
        offerId: isPianoSandbox ? "OFUQZ5ZQO84K" : "OF6O7EMXCMU0",
        subscriptionName: "Monthly Subscription",
        termId: isPianoSandbox ? "TMTPAWVIQQI4" : "TMFWVXLAL1EO",
      },
      subCheckoutHeaderProps: {
        headline: "Checkout",
        bodyText: null,
        showImage: false,
      },
      subCheckoutSelectedSubscriptionProps: {
        selectedInterval: SubscriptionInterval.MONTHLY,
        headline: "Individual Subscription",
        subhead: "1 Seat — Individual Subscription, includes:",
        sellingPoints: [
          "Unlimited access to analysis",
          "Unlimited report downloads",
          "Unlimited chart pack downloads",
        ],
        changePlanToName: SubscriptionName.ONE_SEAT_ANNUAL,
        annualPerMonthPrice: 125,
        annualDiscountPercentage: 16,
        monthlyPerMonthPrice: 149,
        subscriptionPlanFooterText: "$149 per month",
        subtotal: "$149.00",
        dueToday: "$149.00",
        orderSummaryFooterText:
          "Subscriptions automatically renew each month until you cancel. Taxes may apply. You can cancel at any time during your trial in your account settings. Cancellation goes into effect at the end of your current billing period.",
      },
    },
  ],

  /** Monthly w/ trial */
  [
    SubscriptionName.TEN_DAY_FREE_TRIAL_MONTHLY,
    {
      accountOverviewLabel: "Free Trial - Individual Subscription (1 Seat)",
      pianoOffer: {
        offerId: isPianoSandbox ? "OFUQZ5ZQO84K" : "OF6O7EMXCMU0",
        subscriptionName: "Monthly Free Trial",
        termId: isPianoSandbox ? "TMJZ7SIJ9J70" : "TMB8ASZC5SKA",
      },
      subCheckoutHeaderProps: {
        headline: "Start Your Free Trial",
        bodyText:
          "Experience the full value of Morning Consult Pro and get unlimited access to all of our analysis, reports and chart packs fueled by our expansive proprietary survey research data. Start your 10-day free trial today.",
        showImage: true,
      },
      subCheckoutSelectedSubscriptionProps: {
        selectedInterval: SubscriptionInterval.MONTHLY,
        headline: "Morning Consult Pro Free Trial",
        subhead: "1 Seat — Individual Subscription, includes:",
        sellingPoints: [
          "Unlimited access to analysis",
          "Unlimited report downloads",
          "Unlimited chart pack downloads",
        ],
        changePlanToName: SubscriptionName.TEN_DAY_FREE_TRIAL_ANNUAL,
        annualPerMonthPrice: 125,
        annualDiscountPercentage: 16,
        monthlyPerMonthPrice: 149,
        subscriptionPlanFooterText:
          "Free for 10 days then $149 per month after your trial ends.",
        subtotal: "$149.00",
        dueToday: "$0.00",
        orderSummaryFooterText:
          "Subscriptions automatically renew each month until you cancel. Taxes may apply. You can cancel at any time during your trial in your account settings. Cancellation goes into effect at the end of your current billing period.",
      },
    },
  ],

  /** Annually w/o trial */
  [
    SubscriptionName.ONE_SEAT_ANNUAL,
    {
      accountOverviewLabel: "Individual Subscription (1 Seat)",
      pianoOffer: {
        offerId: isPianoSandbox ? "OFUQZ5ZQO84K" : "OF6O7EMXCMU0",
        subscriptionName: "Annual Subscription",
        termId: isPianoSandbox ? "TMFMKMUV5AHK" : "TM9YE4HTX0ZB",
      },
      subCheckoutHeaderProps: {
        headline: "Checkout",
        bodyText: null,
        showImage: false,
      },
      subCheckoutSelectedSubscriptionProps: {
        selectedInterval: SubscriptionInterval.ANNUAL,
        headline: "Individual Subscription",
        subhead: "1 Seat — Individual Subscription, includes:",
        sellingPoints: [
          "Unlimited access to analysis",
          "Unlimited report downloads",
          "Unlimited chart pack downloads",
        ],
        changePlanToName: SubscriptionName.ONE_SEAT_MONTHLY,
        annualPerMonthPrice: 125,
        annualDiscountPercentage: 16,
        monthlyPerMonthPrice: 149,
        subscriptionPlanFooterText:
          "$1,500 every year — save more than $200 by choosing annual pricing",
        subtotal: "$1,500.00",
        dueToday: "$1,500.00",
        orderSummaryFooterText:
          "Subscriptions automatically renew each year until you cancel. Taxes may apply. You can cancel at any time during your trial in your account settings. Cancellation goes into effect at the end of your current billing period.",
      },
    },
  ],

  /** Annually w/ trial */
  [
    SubscriptionName.TEN_DAY_FREE_TRIAL_ANNUAL,
    {
      accountOverviewLabel: "Free Trial - Individual Subscription (1 Seat)",
      pianoOffer: {
        offerId: isPianoSandbox ? "OFUQZ5ZQO84K" : "OF6O7EMXCMU0",
        subscriptionName: "Annual Free Trial",
        termId: isPianoSandbox ? "TMSQUZX1RVCQ" : "TM787C2WS6CL",
      },
      subCheckoutHeaderProps: {
        headline: "Start Your Free Trial",
        bodyText:
          "Experience the full value of Morning Consult Pro and get unlimited access to all of our analysis, reports and chart packs fueled by our expansive proprietary survey research data. Start your 10-day free trial today.",
        showImage: true,
      },
      subCheckoutSelectedSubscriptionProps: {
        selectedInterval: SubscriptionInterval.ANNUAL,
        headline: "Morning Consult Pro Free Trial",
        subhead: "1 Seat — Individual Subscription, includes:",
        sellingPoints: [
          "Unlimited access to analysis",
          "Unlimited report downloads",
          "Unlimited chart pack downloads",
        ],
        changePlanToName: SubscriptionName.TEN_DAY_FREE_TRIAL_MONTHLY,
        annualPerMonthPrice: 125,
        annualDiscountPercentage: 16,
        monthlyPerMonthPrice: 149,
        subscriptionPlanFooterText:
          "Free for 10 days then $1,500 after your trial ends",
        subtotal: "$1,500.00",
        dueToday: "$0.00",
        orderSummaryFooterText:
          "Subscriptions automatically renew each year until you cancel. Taxes may apply. You can cancel at any time during your trial in your account settings. Cancellation goes into effect at the end of your current billing period.",
      },
    },
  ],
]);
